import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatDetail } from "@/lib/api";
import { Loader2, Save, RotateCcw, BarChart3 } from "lucide-react";
import {
  SOCIAL_GENERAL, SHORT_QUOTES, COUNTRY_TEMPLATES,
  FLYER_TEXT, EMAIL_TEMPLATE, SLIDES_OUTLINE,
  AUDIENCE_TEMPLATES, PHONE_SCRIPT,
} from "@/data/marketingTemplates";

/**
 * Admin editor for Marketing Hub template overrides.
 * Each template has a stable id — admin can override any single one
 * without a code push. Empty override = keep the default.
 */
function buildDefaults() {
  const list = [];
  SOCIAL_GENERAL.forEach((body, i) =>
    list.push({ id: `social.general.${i}`, label: `Social · WhatsApp caption ${i + 1}`, defaultBody: body }));
  SHORT_QUOTES.forEach((body, i) =>
    list.push({ id: `social.quote.${i}`, label: `Social · Short quote ${i + 1}`, defaultBody: body }));
  COUNTRY_TEMPLATES.forEach((c) => {
    const id = `social.country.${c.country.toLowerCase().replace(/\s+/g, "-")}`;
    // For structured cards, expose only the headline + quote as override — bullets are locked defaults.
    list.push({ id, label: `Country · ${c.country}`,
      defaultBody: `${c.flag} ${c.country}\n${c.headline}\n\n"${c.quote}"\n${c.tags}` });
  });
  list.push({ id: "print.flyer",   label: "Print · One-page flyer",       defaultBody: FLYER_TEXT });
  list.push({ id: "print.email",   label: "Print · Email template",       defaultBody: EMAIL_TEMPLATE });
  list.push({ id: "print.slides",  label: "Print · Presentation outline", defaultBody: SLIDES_OUTLINE });
  AUDIENCE_TEMPLATES.forEach((a) => {
    const id = `audience.${a.audience.toLowerCase().replace(/[^a-z]+/g, "-")}`;
    list.push({ id, label: `Audience · ${a.audience}`,
      defaultBody: `${a.audience}\n${a.headline}\n\n"${a.quote}"` });
  });
  list.push({ id: "audience.phone", label: "Audience · Phone / in-person script", defaultBody: PHONE_SCRIPT });
  return list;
}

export default function MarketingTemplatesAdmin() {
  const defaults = useMemo(buildDefaults, []);
  const [overrides, setOverrides] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [o, s] = await Promise.all([
          api.get("/admin/marketing/templates"),
          api.get("/admin/marketing/click-stats"),
        ]);
        setOverrides(o.data?.templates || {});
        setUpdatedAt(o.data?.updated_at || null);
        setStats(s.data);
      } catch (e) {
        toast.error(formatDetail(e.response?.data?.detail) || e.message);
      } finally { setBusy(false); }
    })();
  }, []);

  const setOne = (id) => (e) => setOverrides({ ...overrides, [id]: e.target.value });
  const resetOne = (id) => setOverrides(({ [id]: _drop, ...rest }) => rest);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/marketing/templates", { templates: overrides });
      toast.success(`Saved ${data.count} override(s).`);
      setUpdatedAt(data.updated_at);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  if (busy) return <div className="mv-card p-6 flex items-center gap-3"><Loader2 className="animate-spin"/> Loading…</div>;

  return (
    <div className="space-y-5" data-testid="admin-marketing-templates">
      {/* Click stats */}
      <div className="mv-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 size={16} className="mv-green-text"/>
          <div className="font-display text-[16px] mv-green-text">Agent shortlink clicks</div>
        </div>
        {stats && stats.stats.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left" style={{ color: "var(--mv-muted)" }}>
                  <th className="py-2 pr-4">Agent code</th>
                  <th className="py-2 pr-4">Clicks</th>
                  <th className="py-2">Last click</th>
                </tr>
              </thead>
              <tbody>
                {stats.stats.slice(0, 20).map((r) => (
                  <tr key={r.code} className="border-t mv-hairline" data-testid={`admin-mh-click-${r.code}`}>
                    <td className="py-2 pr-4 font-mono">{r.code || "—"}</td>
                    <td className="py-2 pr-4">{r.clicks}</td>
                    <td className="py-2">{r.last_click ? new Date(r.last_click).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 text-[12px]" style={{ color: "var(--mv-muted)" }}>
              Total clicks: <strong>{stats.total_clicks}</strong>
            </div>
          </div>
        ) : (
          <div className="text-[13px]" style={{ color: "var(--mv-muted)" }}>No agent shortlink clicks yet.</div>
        )}
      </div>

      {/* Editor */}
      <div className="mv-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-display text-[17px] mv-green-text">Marketing template overrides</div>
            <p className="text-[12.5px]" style={{ color: "var(--mv-muted)" }}>
              Edit any template's body without a code push. Leave blank to use the default.
              {updatedAt && <> · Last saved: {new Date(updatedAt).toLocaleString()}</>}
            </p>
          </div>
          <button type="button" onClick={save} disabled={saving} className="btn-green" data-testid="admin-mh-save-btn">
            {saving ? <Loader2 className="animate-spin" size={15}/> : <Save size={15}/>}
            {saving ? "Saving…" : "Save all overrides"}
          </button>
        </div>

        <div className="mt-5 space-y-4 max-h-[600px] overflow-y-auto pr-2">
          {defaults.map((t) => {
            const has = Object.prototype.hasOwnProperty.call(overrides, t.id);
            return (
              <div key={t.id} className="border mv-hairline rounded-xl p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[13.5px] font-semibold mv-green-text">{t.label}</div>
                    <div className="text-[11px] font-mono" style={{ color: "var(--mv-muted)" }}>{t.id}</div>
                  </div>
                  {has && (
                    <button type="button" onClick={() => resetOne(t.id)}
                            className="inline-flex items-center gap-1 text-[12px] hover:underline"
                            style={{ color: "var(--mv-muted)" }}
                            data-testid={`admin-mh-reset-${t.id}`}>
                      <RotateCcw size={12}/> Reset to default
                    </button>
                  )}
                </div>
                <textarea
                  rows={Math.min(Math.max(3, t.defaultBody.split("\n").length), 12)}
                  className="mt-2 w-full px-3 py-2 rounded-lg border mv-hairline text-[13px] font-sans"
                  placeholder={t.defaultBody}
                  value={overrides[t.id] || ""}
                  onChange={setOne(t.id)}
                  data-testid={`admin-mh-input-${t.id}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
